/**
 * An order's resolution detail (`GET /auth/orders/:id/resolution`), shared by Portfolio's order
 * lines and History's settled-bets rows: the order's own facts, then the resolution's known fields
 * and its raw JSON (the shape is undocumented — see FRICTION.md). The caller supplies the query
 * state so dev previews can render it from mock data.
 */
import type { OrderResolution } from "@greenbook/client";
import type { OrderLike } from "../lib/portfolio.js";
import { fmtDateTime, fmtMoney } from "../lib/format.js";

export interface ResolutionState {
  data?: OrderResolution;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function ResolutionDetail({ order, state: q }: { order: OrderLike; state: ResolutionState }) {
  return (
    <div className="mt-2 text-[11px]">
      <div className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-dim">
        <KV k="order id" v={order.router_order_id} />
        {order.order_type && <KV k="type" v={order.order_type} />}
        {order.potential_payout != null && <KV k="potential payout" v={fmtMoney(order.potential_payout)} />}
        {order.result && <KV k="result" v={order.result} />}
        {order.settled_datetime && <KV k="settled" v={fmtDateTime(order.settled_datetime)} />}
      </div>
      {q.isLoading ? (
        <span className="text-faint">loading resolution…</span>
      ) : q.isError ? (
        <span className="text-down" title={q.error instanceof Error ? q.error.message : ""}>
          resolution unavailable
          {q.error && typeof q.error === "object" && "code" in q.error ? ` · ${(q.error as { code?: string }).code}` : ""}
        </span>
      ) : q.data ? (
        <ResolutionBody res={q.data} />
      ) : (
        <span className="text-faint">no resolution yet</span>
      )}
    </div>
  );
}

function ResolutionBody({ res }: { res: OrderResolution }) {
  // Shape is undocumented (see FRICTION.md); render known fields, then the raw JSON.
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        {res.result != null && <KV k="result" v={String(res.result)} />}
        {res.settled != null && <KV k="settled" v={String(res.settled)} />}
      </div>
      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded border border-border bg-bg p-2 text-[10px] text-dim">
        {JSON.stringify(res, null, 2)}
      </pre>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <span className="nums max-w-full">
      <span className="text-faint">{k}:</span> <span className="break-all text-dim">{v}</span>
    </span>
  );
}
